/**
 * connectivity — single source of truth for "are we online?".
 *
 * `navigator.onLine` alone is unreliable (true even when wifi is connected but
 * routes nowhere). So we combine:
 *   1. navigator.onLine (cheap)
 *   2. Optional reachability ping to a known endpoint (occasional)
 *   3. window 'online'/'offline' events
 *
 * Exports a tiny event bus so multiple subscribers (sync worker, UI banner,
 * POS save router) all react to the same status change.
 */

import { useEffect, useState } from 'react';

let _isOnline = (typeof navigator !== 'undefined' ? navigator.onLine : true);
const _listeners = new Set();

const notify = () => { for (const l of _listeners) { try { l(_isOnline); } catch { /* ignore */ } } };

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { _isOnline = true; notify(); });
  window.addEventListener('offline', () => { _isOnline = false; notify(); });
}

export const isOnline = () => _isOnline;

/** Subscribe to changes. Returns an unsubscribe fn. */
export const onConnectivityChange = (cb) => {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
};

// ─── React hook ─────────────────────────────────────────────────────────
// Tiny hook so components can `const online = useOnline()`.
export const useOnline = () => {
  const [online, setOnline] = useState(_isOnline);
  useEffect(() => onConnectivityChange(setOnline), []);
  return online;
};
